/**
 * Capa 1 de la Ficha Municipal: el "wow" del Score Alpha.
 *
 * Renderiza el big number 1.27x (o el estado degradado correspondiente),
 * el spectrum visual rojo/ámbar/verde, y el detalle sectorial expandible.
 *
 * Los 3 sub-componentes (ScoreSpectrum, SectorialBreakdown) viven en este
 * archivo porque están tightly-coupled al layout del hero — no se usan
 * desde ningún otro lugar de la app.
 *
 * Estados que maneja:
 * - Loading → ScoreSkeleton (pintado por el padre antes de montar este).
 * - Error 503 → "Indicador en preparación" (job nightly aún no corrió).
 * - Cualquier otro error (500, red) → "No pudimos cargar tu indicador"
 *   con qué hacer (Sprint 21-9; antes se escondía en silencio, #559).
 * - degraded_reason `cat_1_3` → "No aplica a tu categoría".
 * - degraded_reason `no_sgr_data` → "Sin proyectos SGR registrados".
 * - Score normal → big number + spectrum + breakdown.
 *
 * En la ficha de otro municipio (`municipioAjeno`), cada estado lo nombra
 * en vez de hablarle al usuario de "tu" indicador (#726, #746).
 */

import { useRef } from "react";
import { Link, useParams } from "react-router-dom";

import { NombreMunicipio } from "@/components/NombreMunicipio";
import { track } from "@/lib/analytics";
import { ApiError, type MunicipalityScoreDTO, type SectorialScoreDTO } from "@/lib/api";

import { bigIntFromCop, formatCop, scoreColorClass } from "./_shared";

/**
 * Skeleton del hero — usado cuando el profile ya cargó pero el score
 * sigue en vuelo. Visualmente más liviano que `FichaSkeleton` (que
 * cubre toda la página).
 */
export function ScoreSkeleton() {
  return (
    <div
      data-testid="ficha-score-skeleton"
      className="h-40 animate-pulse rounded-2xl bg-slate-100"
    />
  );
}

export function ScoreHero({
  score,
  isLoading,
  error,
  onShowWork,
  municipioAjeno,
}: {
  score: MunicipalityScoreDTO | null;
  isLoading: boolean;
  error: unknown;
  onShowWork: () => void;
  /** Nombre del municipio de la ficha cuando no es el del usuario (issue
   * #726: el ranking regional enlaza a las fichas de los vecinos). Con
   * valor, el hero lo nombra en vez de llamarlo "tu municipio"; null en
   * la ficha propia. */
  municipioAjeno: string | null;
}) {
  if (isLoading) {
    return <ScoreSkeleton />;
  }

  // Caso 503: score aún no precalculado por el job nightly.
  if (error instanceof ApiError && error.status === 503) {
    return (
      <section
        data-testid="ficha-score-not-yet"
        className="rounded-2xl border border-amber-200 bg-amber-50 p-6"
      >
        <p className="text-sm font-semibold text-amber-900">Indicador en preparación</p>
        <p className="mt-1 text-xs text-amber-800">
          Recalculamos{" "}
          {municipioAjeno ? (
            <>
              el nivel de captación de <NombreMunicipio nombre={municipioAjeno} />
            </>
          ) : (
            "tu nivel de captación"
          )}{" "}
          cada noche. Vuelve mañana.
        </p>
      </section>
    );
  }

  // Cualquier otro error (500, timeout, red caída): decir qué pasó y qué
  // hacer en vez de esconder la sección (#559). El usuario no tiene
  // acceso a los logs; el detalle técnico ya lo tiene el backend.
  if (error) {
    return (
      <section
        data-testid="ficha-score-error"
        role="alert"
        className="rounded-2xl border border-red-200 bg-red-50 p-6"
      >
        <p className="text-sm font-semibold text-red-900">
          No pudimos cargar{" "}
          {municipioAjeno ? (
            <>
              el indicador de captación de <NombreMunicipio nombre={municipioAjeno} />
            </>
          ) : (
            "tu indicador de captación"
          )}
        </p>
        <p className="mt-1 text-xs text-red-800">
          Recarga la página. Si sigue fallando, escríbenos a soporte@kaptar.com.co.
        </p>
      </section>
    );
  }

  if (!score) return null;

  // Ficha degradada cat 1-3 — el peer group de capitales no es comparable.
  if (score.degraded_reason === "cat_1_3") {
    return (
      <section
        data-testid="ficha-score-degraded-cat"
        className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
      >
        <p className="text-sm font-semibold text-slate-700">
          Este indicador no aplica a {municipioAjeno ? "su" : "tu"} categoría
        </p>
        <p className="mt-1 text-xs text-slate-500">
          {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu municipio"} es
          categoría 1-3 (ciudad grande). El indicador está calibrado para categorías 4-6, donde
          compiten por recursos SGR de forma comparable. El contexto histórico de abajo sigue siendo
          útil.
        </p>
      </section>
    );
  }

  // Ficha degradada por falta de SGR data — muni sin proyectos en historial.
  // Issue #561: el texto solo anuncia la comparación si hay contra quién
  // (la tarjeta de brecha de abajo aparece cuando existe un mejor par).
  if (score.degraded_reason === "no_sgr_data") {
    const hayMejorPar = score.peer_best_dane !== null;
    return (
      <section
        data-testid="ficha-score-no-sgr"
        className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
      >
        <p className="text-sm font-semibold text-slate-700">
          {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu municipio"} no tiene
          proyectos SGR registrados
        </p>
        <p className="mt-1 text-xs text-slate-500">
          {hayMejorPar
            ? `Abajo ves lo que captó el mejor municipio de ${municipioAjeno ? "su" : "tu"} grupo — `
            : null}
          <Link to="/radar" className="text-brand-700 underline">
            {hayMejorPar ? "revisa el Radar" : "Revisa el Radar"}
          </Link>{" "}
          para presentarte a convocatorias.
        </p>
      </section>
    );
  }

  // Score normal — el wow (Sprint Pre-Demo Alcaldesa Fase 2: porcentaje + narrativa).
  const scoreColor = scoreColorClass(score.score_alpha);
  const percentage = score.score_alpha !== null ? (score.score_alpha * 100).toFixed(1) : null;
  const pill =
    score.score_alpha !== null
      ? narrativePill(score.score_alpha, municipioAjeno ? "sus" : "tus")
      : null;

  return (
    <section
      data-testid="ficha-score-hero"
      className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold tracking-wider text-slate-500 uppercase">
            {municipioAjeno ? `Desempeño de ${municipioAjeno} en SGR` : "Tu desempeño en SGR"}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            Comparado con municipios similares · captación histórica completa
          </p>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              data-testid="ficha-score-big-number"
              className={`text-7xl leading-none font-extrabold tracking-tight ${scoreColor}`}
            >
              {percentage !== null ? `${percentage}%` : "—"}
            </span>
          </div>
          <p className="mt-2 text-sm font-medium text-slate-700">
            del promedio que captan municipios como{" "}
            {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "el tuyo"}
          </p>

          {pill !== null && (
            <div
              data-testid="ficha-score-narrative-pill"
              className={`mt-4 rounded-md border-l-4 px-4 py-2 ${pill.box}`}
            >
              <p className={`text-sm font-semibold ${pill.title}`}>{pill.text}</p>
              <p className={`mt-1 text-xs ${pill.detail}`}>
                {municipioAjeno ? <NombreMunicipio nombre={municipioAjeno} /> : "Tu muni"}:{" "}
                {formatCop(bigIntFromCop(score.captacion_total_cop))} · Promedio peers:{" "}
                {formatCop(bigIntFromCop(score.peer_avg_captacion_cop))}
              </p>
            </div>
          )}

          <p className="mt-3 text-xs text-slate-500">
            Grupo de comparación: {score.peer_group_definition}
          </p>
        </div>

        <button
          type="button"
          onClick={onShowWork}
          data-testid="ficha-show-work-link"
          className="inline-flex items-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition hover:border-brand-300 hover:bg-brand-100"
        >
          ¿Cómo se calcula?
        </button>
      </div>

      {/* Spectrum bar — sin cambios */}
      {score.score_alpha !== null && <ScoreSpectrum score={score.score_alpha} />}

      {/* Footer con ranking */}
      {score.rank_in_peer_group !== null && (
        <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-600">
          {/* +1: peer_group_size excluye al muni mismo (backend filtra con
              AND dane_code != target en peer_group.py). El total mostrado
              al usuario incluye al muni. */}
          Puesto {score.rank_in_peer_group} de {score.peer_group_size + 1}
          {score.percentile !== null && ` · percentil ${score.percentile}`}
        </p>
      )}

      {/* Detalle sectorial */}
      {score.sectorial_scores.length > 0 && <SectorialBreakdown sectors={score.sectorial_scores} />}
    </section>
  );
}

/**
 * Texto y tono de la píldora bajo el big number, según el lado del
 * promedio en que cae el score (#725: antes solo contemplaba score < 1 y
 * con 1.474 decía "-47.4% menos" en rojo). Se compara la diferencia ya
 * redondeada para no pintar "0.0% menos" en el promedio exacto.
 */
function narrativePill(scoreAlpha: number, posesivo: "tus" | "sus") {
  const diff = scoreAlpha * 100 - 100;
  const pct = Math.abs(diff).toFixed(1);
  if (pct === "0.0") {
    return {
      text: `En el promedio de ${posesivo} pares`,
      box: "border-slate-400 bg-slate-50",
      title: "text-slate-900",
      detail: "text-slate-600",
    };
  }
  if (diff > 0) {
    return {
      text: `${pct}% más que el promedio de ${posesivo} pares`,
      box: "border-emerald-500 bg-emerald-50",
      title: "text-emerald-900",
      detail: "text-emerald-700",
    };
  }
  return {
    text: `${pct}% menos que el promedio de ${posesivo} pares`,
    box: "border-red-500 bg-red-50",
    title: "text-red-900",
    detail: "text-red-700",
  };
}

/**
 * Barra "spectrum" con marcador del score actual.
 *
 * Tres tramos coloreados (rojo bottom / ámbar mid / verde top) con
 * un indicador en la posición del score. Funciona como gauge sin
 * librería de charts.
 *
 * Mapeo: 0.5x → 0%, 1.0x → 50%, 1.5x → 100%. Por encima de 1.5 se
 * clampa al borde derecho; por debajo de 0.5 al izquierdo.
 */
function ScoreSpectrum({ score }: { score: number }) {
  const clamped = Math.max(0.5, Math.min(1.5, score));
  const position = ((clamped - 0.5) / 1.0) * 100;

  return (
    <div className="mt-6" data-testid="ficha-score-spectrum">
      <div className="relative h-2 overflow-hidden rounded-full">
        {/* Tramo rojo: 0% — 40% (0.5x — 0.9x) */}
        <div className="absolute inset-y-0 left-0 w-[40%] bg-red-200" />
        {/* Tramo ámbar: 40% — 60% (0.9x — 1.1x) */}
        <div className="absolute inset-y-0 left-[40%] w-[20%] bg-amber-200" />
        {/* Tramo verde: 60% — 100% (1.1x — 1.5x+) */}
        <div className="absolute inset-y-0 right-0 left-[60%] bg-emerald-200" />
        {/* Marcador del score */}
        <div
          className="absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-900 shadow-sm"
          style={{ left: `${position}%` }}
          aria-hidden="true"
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
        <span>0.5x</span>
        <span>0.9x</span>
        <span>1.1x</span>
        <span className="text-right">1.5x+</span>
      </div>
    </div>
  );
}

/**
 * Detalle sectorial dentro del hero — colapsado por default.
 *
 * Dispara `municipality_score_expanded` 1 vez por sesión cuando el
 * usuario despliega el `<details>`. El guard con `expandedRef` evita
 * inflar el conteo si el usuario abre/cierra varias veces.
 */
function SectorialBreakdown({ sectors }: { sectors: SectorialScoreDTO[] }) {
  const { dane } = useParams<{ dane: string }>();
  const expandedRef = useRef(false);
  const handleToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const open = (e.currentTarget as HTMLDetailsElement).open;
    if (open && !expandedRef.current) {
      expandedRef.current = true;
      track({
        event_name: "municipality_score_expanded",
        properties: {
          dane_code: dane,
          sector_count: sectors.length,
        },
      });
    }
  };
  return (
    <details
      data-testid="ficha-sectorial-breakdown"
      className="mt-6 border-t border-slate-100 pt-4"
      onToggle={handleToggle}
    >
      <summary className="cursor-pointer text-xs font-semibold tracking-wide text-slate-500 uppercase select-none">
        Detalle por sector ({sectors.length})
      </summary>
      <div className="mt-3 space-y-2">
        {sectors.map((s) => {
          const score = s.score;
          const color = scoreColorClass(score);
          return (
            <div
              key={s.sector}
              data-testid="ficha-sectorial-item"
              className="flex items-center gap-3"
            >
              <span className="w-32 truncate text-xs font-medium text-slate-700">
                {s.sector.toLowerCase()}
              </span>
              <div className="flex-1">
                <div className="relative h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-brand-500"
                    style={{
                      width: `${Math.min(100, Math.max(5, (score ?? 0) * 40))}%`,
                    }}
                  />
                  {/* Línea vertical en 1.0x (promedio del grupo de comparación) */}
                  <div className="absolute inset-y-0 w-px bg-slate-400" style={{ left: "40%" }} />
                </div>
              </div>
              <span className={`w-16 text-right text-xs font-semibold ${color}`}>
                {score !== null ? `${score.toFixed(2)}x` : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </details>
  );
}
